import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import { Spin, Empty, Select } from "antd";
import moment from "moment";
import {
    HiOutlineUserAdd,
    HiOutlineCalendar,
    HiOutlineClock,
    HiOutlineTrendingUp,
    HiOutlineClipboardList,
    HiOutlineChevronRight,
    HiOutlineUser
} from 'react-icons/hi';
import DashboardChart from "./DashboardChart";
import { decryptData } from '../Crypto_Helpers/cryptoHelper';
import { initEncryptionSession } from '../../encryptionSession';

const { Option } = Select;

function DashboardMasterComponent() {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        appointments: 0,
        queue: 0,
        services: 0,
        revenue: 0
    });
    const [upcomingAppointments, setUpcomingAppointments] = useState([]);
    const [revenueData, setRevenueData] = useState([]);
    const [doctorClinics, setDoctorClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState(null);
    const [permissions, setPermissions] = useState({
        appointment_access: 1,
        service_access: 1,
        workinghrs_access: 1,
        revenue_access: 1
    });

    useEffect(() => {
        fetchClinics();
    }, []);

    useEffect(() => {
        if (selectedClinic) {
            fetchDashboardData();
        }
    }, [selectedClinic]);

    const fetchClinics = async () => {
        try {
            const doctorId = Cookies.get('Doctor_id') || Cookies.get('doctor_id') || localStorage.getItem('Doctor_id');
            const response = await axiosInstance.post("doctor/getDoctorClinics", { doctorId: doctorId });
            if (response.data?.status === 1 && Array.isArray(response.data.data)) {
                const clinics = response.data.data;
                setDoctorClinics(clinics);
                const savedClinicId = Cookies.get('clinic_id') || Cookies.get('Clinic_id');
                const initialClinic = clinics.find(c => c.clinicId == savedClinicId) || clinics[0];
                if (initialClinic) {
                    handleClinicChange(initialClinic.clinicId, clinics);
                }
            }
        } catch (err) {
            console.error("Failed to fetch clinics", err);
        }
    };

    const handleClinicChange = (clinicId, list = doctorClinics) => {
        const clinic = list.find(c => c.clinicId == clinicId);
        if (!clinic) return;
        setSelectedClinic(clinic);
        Cookies.set('pharmacy_id', clinic.pharmacyId || '');
        Cookies.set('clinic_id', clinic.clinicId);
        Cookies.set('Clinic_name', clinic.clinicName || '');
        Cookies.set('tax_value', clinic.tax_value || '');
        Cookies.set('Specialty_name', clinic.specialityname || '');
    };

    const fetchDashboardData = async () => {
        if (!selectedClinic) return;
        setLoading(true);
        try {
            const clinicId = selectedClinic.clinicId;
            const doctorId = Cookies.get('Doctor_id') || Cookies.get('doctor_id') || localStorage.getItem('Doctor_id');
            const todayStr = moment().format('YYYY-MM-DD');

            const [appointmentsRes, revenueRes, servicesRes, permRes] = await Promise.all([
                axiosInstance.post("doctor/getAppointmentList", {
                    doctor_id: doctorId,
                    clinic_id: clinicId,
                    search_date: todayStr,
                    search_date_to: todayStr,
                    period: "day",
                    patient_type: ""
                }),
                axiosInstance.post("doctor/getRevenueForDoctor", { clinic_id: clinicId, year: moment().year(), doctorId: doctorId }),
                axiosInstance.post("doctor/getDoctorService", { doctor_id: doctorId }),
                axiosInstance.post("doctor/clinicTabPermissionbyId", { doctorId, clinicId })
            ]);

            if (permRes.data?.status === 1 && Array.isArray(permRes.data.data) && permRes.data.data.length > 0) {
                setPermissions(permRes.data.data[0]);
            }

            if (appointmentsRes.data?.status === 1 && appointmentsRes.data?.data) {
                const apiData = appointmentsRes.data.data;
                let result = [];
                if (Array.isArray(apiData) && apiData.length > 0) {
                    if (Array.isArray(apiData[0]?.result)) {
                        result = apiData[0].result;
                    } else if (typeof apiData[0]?.result === 'string') {
                        try {
                            const aesKey = await initEncryptionSession();
                            result = await decryptData(apiData[0].result, aesKey);
                        } catch (e) {
                            result = [];
                        }
                    } else {
                        result = apiData;
                    }
                } else if (Array.isArray(apiData)) {
                    result = apiData;
                }
                const validList = (Array.isArray(result) ? result : []).map(a => ({
                    ...a,
                    patientName: a.patientName || a.patient_name || a.name || '-',
                    patientId: a.patientId || a.patient_id || a.id || '',
                    bookingId: a.bookingId || a.booking_id || a.id || '',
                    book_date: a.book_date || a.appointment_datetime || '',
                    from_time: a.from_time || a.appointment_time || '',
                    service_name: a.service_name || a.serviceName || a.service || '-',
                }));
                setUpcomingAppointments(validList.slice(0, 5));
                setStats(prev => ({
                    ...prev,
                    appointments: validList.length,
                    queue: validList.filter(a => a.check_in_status === 1 && a.check_out_status === 0).length
                }));
            }

            if ((revenueRes.data?.status === 200 || revenueRes.data?.status === 1) && Array.isArray(revenueRes.data.data)) {
                const resList = revenueRes.data.data;
                const totalRev = resList.reduce((acc, curr) => acc + (curr.booking_count || curr.total_amount || curr.revenue || 0), 0);
                setStats(prev => ({ ...prev, revenue: totalRev }));

                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const mappedData = months.map((m, idx) => {
                    const found = resList.find(item => item.month === idx + 1 || item.month === m || item.month_name === m);
                    return {
                        month: m,
                        value: found ? (found.booking_count || found.total_amount || found.revenue || 0) : (resList[idx]?.booking_count || 0)
                    };
                });
                setRevenueData(mappedData);
            }

            if (servicesRes.data?.status === 1 && Array.isArray(servicesRes.data.data)) {
                setStats(prev => ({ ...prev, services: servicesRes.data.data.length }));
            }

        } catch (err) {
            console.error("Dashboard data fetch failed", err);
        } finally {
            setLoading(false);
        }
    };

    const actionCards = [
        ...(permissions.appointment_access != 0 ? [{ title: 'New Appointment', icon: HiOutlineUserAdd, color: 'bg-blue-500', path: '/dashboard/walkinbooking' }] : []),
        ...(permissions.service_access != 0 ? [{ title: 'Manage Services', icon: HiOutlineClipboardList, color: 'bg-indigo-500', path: '/dashboard/services' }] : []),
        { title: 'Manage Queue', icon: HiOutlineClipboardList, color: 'bg-teal-500', path: '/dashboard/queue' },
        ...(permissions.workinghrs_access != 0 ? [{ title: 'Working Hours', icon: HiOutlineClock, color: 'bg-purple-500', path: '/dashboard/workinghours' }] : []),
        ...(permissions.revenue_access != 0 ? [{ title: 'Revenue Reports', icon: HiOutlineTrendingUp, color: 'bg-amber-500', path: '/dashboard/revenue' }] : []),
    ];

    if (loading) return (
        <div className="h-64 flex flex-col items-center justify-center gap-2">
            <Spin size="small" />
            <p className="text-gray-400 font-medium text-xs">Loading Dashboard...</p>
        </div>
    );

    return (
        <div className="space-y-2.5 animate-in fade-in duration-300 w-full text-gray-800">
            {/* Integrated Header Bar - Strictly Left Title & Strictly Right Actions */}
            <div
                className="bg-white px-4 py-2.5 rounded-xl border border-gray-200 shadow-2xs flex flex-col sm:flex-row flex-nowrap items-start sm:items-center justify-between w-full box-border gap-2 sm:gap-0"
            >
                <div style={{ textAlign: 'left', flexShrink: 0 }}>
                    <h2 className="text-sm font-bold text-gray-800 leading-tight m-0">
                        Good {moment().hour() < 12 ? 'Morning' : moment().hour() < 17 ? 'Afternoon' : 'Evening'},
                        <span className="text-teal-600"> {Cookies.get('Doctor_name')?.replace(/"/g, '') || Cookies.get('doctor_name')?.replace(/"/g, '') || 'Doctor'}</span>
                    </h2>
                    <p className="text-gray-400 text-[11px] m-0 font-medium">Here's what's happening at your clinic today.</p>
                </div>

                <div className="flex flex-row items-center gap-2 shrink-0 w-full sm:w-auto">
                    <div className="bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-md flex items-center gap-1.5 shadow-2xs flex-1 sm:flex-none">
                        <HiOutlineUser className="text-teal-600 shrink-0" size={14} />
                        <Select
                            value={selectedClinic?.clinicId}
                            onChange={(val) => handleClinicChange(val)}
                            variant="borderless"
                            size="small"
                            className="min-w-[140px] w-full font-bold text-xs"
                        >
                            {doctorClinics.map(doc => (
                                <Option key={doc.clinicId} value={doc.clinicId}>
                                    <span className="font-bold text-xs text-gray-800">{doc.clinicName}</span>
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-md flex items-center gap-1.5 text-xs font-semibold text-gray-700 whitespace-nowrap">
                        <HiOutlineCalendar className="text-teal-600 shrink-0" size={14} />
                        <span>{moment().format('DD MMM, YYYY')}</span>
                    </div>
                </div>
            </div>

            {/* Quick Stats Grid - Compact Horizontal */}
            <div className="w-full">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                    {[
                        { label: "Today's Appointments", value: stats.appointments, icon: HiOutlineCalendar, color: 'from-blue-500 to-indigo-600' },
                        { label: 'Active Services', value: stats.services, icon: HiOutlineClipboardList, color: 'from-teal-500 to-emerald-600' },
                        { label: 'Patient Queue', value: stats.queue, icon: HiOutlineUser, color: 'from-purple-500 to-pink-600' },
                        { label: 'Total Revenue', value: stats.revenue, icon: HiOutlineTrendingUp, color: 'from-amber-500 to-orange-600' },
                    ].map((stat, i) => (
                        <div key={i} className="bg-white p-3 rounded-lg border border-gray-200 shadow-2xs hover:border-teal-200 transition-all">
                            <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate m-0">{stat.label}</p>
                                    <p className="text-base font-bold text-gray-800 leading-tight mt-0.5 m-0">{stat.value}</p>
                                </div>
                                <div className={`bg-gradient-to-br ${stat.color} p-1.5 rounded-md text-white shadow-2xs shrink-0`}>
                                    <stat.icon size={15} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Action Cards */}
            <div className="w-full pt-1.5 pb-2">
                <div className="flex flex-wrap gap-2.5">
                    {actionCards.map((action, i) => (
                        <button
                            key={i}
                            onClick={() => history.push(action.path)}
                            className="flex-1 min-w-[120px] bg-white p-2.5 rounded-lg border border-gray-200 shadow-2xs hover:border-teal-200 transition-all flex flex-col items-center justify-center gap-1.5 cursor-pointer group"
                        >
                            <div className={`${action.color} p-1.5 rounded text-white shadow-2xs group-hover:scale-110 transition-transform`}>
                                <action.icon size={15} />
                            </div>
                            <span className="text-[10px] font-bold text-gray-700 m-0 text-center leading-tight">{action.title}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content Area - Split Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-stretch w-full">
                {/* Visual Chart (Left) */}
                <div className="col-span-1 lg:col-span-8 bg-white p-3.5 rounded-lg border border-gray-200 shadow-2xs flex flex-col h-full w-full">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className="font-bold text-gray-800 text-xs m-0">Revenue Trend (Last 7 Days)</h3>
                            <p className="text-[10px] text-gray-400 m-0">Daily earnings distribution</p>
                        </div>
                    </div>
                    <div className="flex-1 w-full h-36 md:h-48 relative">
                        {loading ? (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Spin size="small" />
                            </div>
                        ) : (
                            <DashboardChart data={revenueData} />
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white p-3.5 rounded-lg border border-gray-200 shadow-2xs space-y-2">
                    <div>
                        <h3 className="font-bold text-gray-800 text-xs m-0">Quick Actions</h3>
                        <p className="text-[10px] text-gray-400 m-0">Frequent tasks & shortcuts</p>
                    </div>

                    <div className="space-y-1.5">
                        {actionCards.map((card, i) => (
                            <button
                                key={i}
                                onClick={() => history.push(card.path)}
                                className="w-full flex items-center gap-2 bg-gray-50/70 hover:bg-teal-50/50 p-1.5 rounded-md border border-gray-100 hover:border-teal-200 transition-all group text-left cursor-pointer"
                            >
                                <div className={`${card.color} p-1.5 rounded text-white shadow-2xs shrink-0 group-hover:scale-105 transition-transform`}>
                                    <card.icon size={14} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-gray-800 text-xs group-hover:text-teal-700 transition-colors m-0">
                                        {card.title}
                                    </p>
                                    <p className="text-[10px] text-gray-400 truncate m-0">Quick access to module</p>
                                </div>
                                <HiOutlineChevronRight size={13} className="text-gray-400 group-hover:text-teal-600 transition-colors shrink-0" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent Appointments Table - Compact */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-2xs overflow-hidden">
                <div className="px-3.5 py-2 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h3 className="font-bold text-gray-800 text-xs m-0">Recent Appointments</h3>
                        <p className="text-[10px] text-gray-400 m-0">Today's scheduled appointments</p>
                    </div>
                    <button
                        onClick={() => history.push('/dashboard/appointments')}
                        className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 text-xs font-bold transition-colors cursor-pointer border-none bg-transparent"
                    >
                        View All
                        <HiOutlineChevronRight size={13} />
                    </button>
                </div>

                <div className="overflow-x-auto">
                    {upcomingAppointments.length > 0 ? (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 text-gray-500 text-[9px] uppercase font-bold tracking-wider border-b border-gray-100">
                                <tr>
                                    <th className="px-3.5 py-2">Patient Name</th>
                                    <th className="px-3.5 py-2">Date</th>
                                    <th className="px-3.5 py-2">Time</th>
                                    <th className="px-3.5 py-2">Service</th>
                                    <th className="px-3.5 py-2">Status</th>
                                    <th className="px-3.5 py-2 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-xs">
                                {upcomingAppointments.map((app, i) => {
                                    const statusMap = {
                                        '0': { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
                                        '1': { label: 'Confirmed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                                        '2': { label: 'Completed', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
                                        '3': { label: 'Cancelled', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
                                        'Waiting': { label: 'Waiting', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                                    };
                                    const st = statusMap[String(app.status)] || { label: app.status || 'Confirmed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
                                    
                                    return (
                                        <tr key={i} className="hover:bg-teal-50/20 transition-colors">
                                            <td className="px-3.5 py-1.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-5.5 h-5.5 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center font-bold text-[10px] shrink-0">
                                                        {app.patientName ? app.patientName.charAt(0).toUpperCase() : <HiOutlineUser size={11} />}
                                                    </div>
                                                    <span className="font-bold text-gray-800 text-xs">{app.patientName}</span>
                                                </div>
                                            </td>
                                            <td className="px-3.5 py-1.5 text-xs font-medium text-gray-600">
                                                {app.book_date ? String(app.book_date).split('T')[0] : '-'}
                                            </td>
                                            <td className="px-3.5 py-1.5 text-xs font-medium text-gray-600">
                                                {app.from_time || '-'}
                                            </td>
                                            <td className="px-3.5 py-1.5 text-xs font-medium text-gray-600">
                                                {app.service_name}
                                            </td>
                                            <td className="px-3.5 py-1.5">
                                                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${st.cls}`}>
                                                    {st.label}
                                                </span>
                                            </td>
                                            <td className="px-3.5 py-1.5 text-right">
                                                <button
                                                    onClick={() => history.push(`/dashboard/appointments/report/${app.patientId}/${app.bookingId}/${app.book_date}`)}
                                                    className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-800 text-xs font-bold hover:underline cursor-pointer border-none bg-transparent"
                                                >
                                                    View
                                                    <HiOutlineChevronRight size={13} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="py-6 flex flex-col items-center justify-center text-gray-400">
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span className="text-xs font-medium text-gray-400">No appointments found for today</span>} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DashboardMasterComponent;